import React, { useEffect, useState } from 'react'

const API_URL = process.env.REACT_APP_BACKEND_URL

/**
 * Public social-proof counter.
 * Displays: "Učí sa s nami už 1 524 žiakov"
 *
 * The number comes from GET /api/public/registered-users. The initial
 * render shows the baseline 1 524 so visitors never see a flash of "0".
 * A subtle count-up on first mount is applied; the final value is the
 * authoritative backend value, never fabricated.
 */
const RegisteredUsersCounter = () => {
  const [count, setCount] = useState(1524)
  const [animated, setAnimated] = useState(1524)

  useEffect(() => {
    let cancelled = false
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/registered-users`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        })
        if (!res.ok) return
        const data = await res.json()
        if (cancelled) return
        if (typeof data?.registered_users === 'number' && data.registered_users >= 0) {
          setCount(data.registered_users)
        }
      } catch (_e) {
        // Fail soft — keep the baseline. Never break the homepage.
      }
    }
    fetchCount()
    return () => { cancelled = true }
  }, [])

  // Subtle count-up transition: only animates from the current displayed
  // value to the new authoritative value, over ~600ms. Never fakes a trend.
  useEffect(() => {
    if (animated === count) return
    const start = animated
    const diff = count - start
    const duration = 600
    const t0 = performance.now()
    let raf
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      const value = Math.round(start + diff * eased)
      setAnimated(value)
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => raf && cancelAnimationFrame(raf)
  }, [count]) // eslint-disable-line react-hooks/exhaustive-deps

  // Slovak number format: thousands separated by a thin space.
  const formatted = animated.toLocaleString('sk-SK').replace(/\u00A0/g, ' ')

  return (
    <section
      data-testid="registered-users-counter"
      aria-label="Počet registrovaných žiakov"
      className="mt-5 md:mt-6 flex justify-center animate-fadeIn"
    >
      <div className="inline-flex items-center gap-2.5 sm:gap-3 rounded-full
                      bg-white/80 backdrop-blur-sm border border-emerald-100
                      ring-1 ring-emerald-50 shadow-sm
                      px-4 py-2 sm:px-5 sm:py-2.5">
        <span
          className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full
                     bg-gradient-to-br from-emerald-500 to-green-600
                     text-white flex items-center justify-center shadow-sm"
          aria-hidden="true"
        >
          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </span>
        <p className="text-sm sm:text-base font-medium text-gray-800 leading-snug">
          Učí sa s nami už{' '}
          <span
            data-testid="registered-users-count"
            className="font-extrabold text-emerald-700 tabular-nums"
          >
            {formatted}
          </span>{' '}
          žiakov
        </p>
      </div>
    </section>
  )
}

export default RegisteredUsersCounter
